import { useState } from "react";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { AdminOrgDetail } from "@/hooks/queries/admin-orgs-types";
import { useRenewContract } from "@/hooks/queries/useRenewContract";
import { formatDate } from "@/lib/format-date";
import {
  SEAT_CAPACITY_MAX_MESSAGE,
  SEAT_CAPACITY_MIN_MESSAGE,
  SEAT_CAPACITY_WHOLE_NUMBER_MESSAGE,
} from "@/routes/admin/AdjustSeatCapacityControl";
import { toAdminOrgError, type AdminOrgBanner } from "@/routes/admin/admin-orgs-error";
import { EMPTY_CELL } from "@/routes/admin/admin-orgs-derivations";
import {
  requiredDate,
  SEAT_CAPACITY_MAX,
  SEAT_CAPACITY_MIN,
} from "@/routes/admin/create-organization-schema";

// Story 21.1 — the Contract card's `renewControl` slot on Screen 24 (FR87).
//
// THE TERM BOUNDARY, and the third write on this card. `extend-contract` moves
// the end date inside the current term and `adjust-seats` moves the pool inside
// it; this one CLOSES a term and opens the next, writing the new end date, the
// new term's start, the new capacity and a reset consumption in ONE statement.
// It is the only path in the product that lowers `seat_consumed`, which is why
// it is a separate endpoint rather than a flag on either sibling (D4).
//
// TWO WRITABLE FIELDS, one dialog, one POST — unlike both siblings, which
// submit a single value. They cannot be split: a boundary that set the date
// without the seat count would leave consumption re-established at a number
// nobody chose, and the check constraint refuses the two halves as separate
// statements anyway (D3).
//
// EACH FIELD OWNS ITS OWN INLINE ERROR, with its own id and its own
// `role="alert"`. Both can be wrong at once — an earlier date AND a seat count
// below the floor — and a shared error channel would show one refusal and
// silently drop the other, sending the operator round the loop twice.
//
// NO CLIENT-SIDE FLOOR GUARD, and the asymmetry with `AdjustSeatCapacityControl`
// is the point. That control refuses below `seat_consumed` client-side because
// FR83 makes that counter one-way within a term: its rendered figure can be
// stale-LOW but never stale-high, so the guard can only refuse what the server
// would also refuse. `active_seat_count` has no such property — a seat filled
// since this page loaded raises it, a seat removed lowers it — so a client guard
// here would refuse values the server accepts, with no server test able to catch
// it. The floor is shown, never enforced; ORG-022 is the authority (AC29).
//
// THE CLIENT DOES NOT OWN THE CLOCK, for the reason `ExtendContractControl`
// already records: no past-date refusal and no mirror of the server's
// `le=9000-12-31`, because the server compares in UTC and a browser-local
// comparison disagrees by up to a day at the boundary. There is likewise no
// client-side strictly-forward check — same clock, same boundary, and ORG-001
// lands inline on the date field.
//
// NO ROLE BRANCH, matching both siblings: `/admin/orgs` is fenced by
// `route-access.ts` and by the `SUPER_ADMIN_ROLES` `RoleGuard`, and the backend
// capability decides the request.

export const RENEW_CONTRACT_TRIGGER_LABEL = "Renew contract";
export const RENEW_CONTRACT_SUBMIT_LABEL = "Start the new term";
export const RENEW_CONTRACT_PENDING_LABEL = "Renewing\u2026";

/**
 * What the pending LOCK says to assistive technology.
 *
 * While the renewal is in flight this dialog refuses every dismissal (see
 * `closeDialog`) and both footer buttons carry native `disabled`, which takes
 * them out of the tab order — so the submit label's flip to
 * `RENEW_CONTRACT_PENDING_LABEL` is a visual-only signal announced to nobody. A
 * keyboard or screen-reader operator presses Escape, gets silence, and cannot
 * tell a deliberate lock from a dialog that has broken. This sentence is that
 * difference: what is happening, and why the dialog will not close yet.
 */
export const RENEW_CONTRACT_PENDING_ANNOUNCEMENT =
  "Renewing the contract. This dialog stays open until the renewal finishes.";

export const RENEW_CONTRACT_END_DATE_LABEL = "New contract end date";
export const RENEW_CONTRACT_SEATS_LABEL = "Seats for the new term";
export const RENEW_CONTRACT_CARRIED_LABEL = "Seats carrying into the new term";
export const RENEW_CONTRACT_CURRENT_TERM_LABEL = "Current term";

export const RENEW_CONTRACT_DESCRIPTION =
  "Closes the current term and opens a new one with its own seat count.";

export const RENEW_CONTRACT_DATE_REQUIRED_MESSAGE = "Enter the new term's end date.";

/**
 * SCP §4D's confirmation copy — what the operator is about to cause, before
 * they cause it.
 *
 * Both halves are load-bearing and neither is obvious from the fields. The
 * reset is the whole point of the write and is invisible in a form that asks
 * only for a date and a number; the release is the FR83 rule going the other
 * way for the first time, and an operator who has only ever seen "a removed
 * seat stays consumed" will not assume a boundary undoes it.
 *
 * IT DOES NOT SAY THE WRITE IS IRREVERSIBLE, though it is. No endpoint puts a
 * renewal back, but saying so on the dialog would invite the question of how to
 * ask for one, and no answer exists to give.
 *
 * THE THIRD SENTENCE IS NEW COPY, ordered by the PM on 2026-09-16 (Franklin#829)
 * and not drift from the Screen 24 mockup, which is silent on when a term
 * starts. It exists because Story 21.5 made the answer something an operator
 * can be surprised by: renewing before the end date does not schedule the new
 * term for that date — it starts the term TODAY, so the new seat count and
 * every release in the closing term take effect immediately, for the remainder
 * of a period the organization has already paid for. That is the one
 * consequence of this dialog nothing else on the screen states.
 */
export const RENEW_CONTRACT_CONFIRMATION =
  "When the new term opens, consumption is re-established at the seats carrying into it. Seats released during the closing term are released with it and stop counting. Renewing before the current end date starts the new term today, so the new seat count applies from today — including the seats released in the term that is ending.";

/**
 * AC27's headroom state — the requested count is ABOVE the carried headcount.
 *
 * Rendered from the typed value, so it moves as the operator types and answers
 * the question the two read-only figures above it raise but do not close: what
 * the pool will look like on the first day of the new term.
 */
export function renewalHeadroomMessage(free: number): string {
  return free === 1
    ? "The new term starts with 1 free seat."
    : `The new term starts with ${free} free seats.`;
}

/**
 * AC27's full-pool state — requested EQUALS the carried headcount.
 *
 * Legal, and the server accepts it, which is exactly why it needs saying: the
 * new term opens already at its limit and the next invitation is refused. The
 * sentence names the remedy that actually exists (raise capacity) rather than
 * implying the renewal should be re-typed.
 */
export const RENEWAL_FULL_POOL_MESSAGE =
  "The new term starts with no free seats — invitations are refused until capacity rises.";

/**
 * Whether this organization can be renewed AT ALL — AC27's two "not offered"
 * states, in one predicate beside the control they gate.
 *
 * Exported and applied at the `OrgDetailScreen` call site rather than by
 * returning `null` from the component, because `ContractCard` renders the slot
 * WRAPPER whenever its prop is non-null, and a persistent empty
 * `<div data-slot="renew-contract-control">` is an element that exists only to
 * be found by a selector — the shape that card's contract explicitly refuses.
 *
 * TWO reasons, one answer:
 *   • a PERSONAL organization has no B2B contract to renew (the server refuses
 *     ORG-002), so the control is not rendered at all;
 *   • a professional organization with NO stored end date has no term to close,
 *     so there is no boundary to stamp `current_term_started_at` from and the
 *     server refuses ORG-001 (boundary row 2). Such a record is repaired with
 *     `extend-contract`, which is still offered.
 *
 * Written as a TYPE PREDICATE so the mount gets the non-null
 * `contract_end_date` out of the same test that decides whether to render. A
 * plain `boolean` would force the call site to repeat the null check purely to
 * satisfy the compiler, and a rule with two copies is a rule that drifts.
 */
export function canRenewContract(
  organization: AdminOrgDetail,
): organization is AdminOrgDetail & { contract_end_date: string } {
  return organization.org_type === "professional" && organization.contract_end_date !== null;
}

// One date grammar with two client sources is one that drifts —
// `ExtendContractControl` takes `requiredDate` from the same place for the same
// reason. It supplies both client-side messages: the missing-value one passed in
// here, and its own "Enter a valid date." for a value that is not `YYYY-MM-DD`.
const endDateShape = requiredDate(RENEW_CONTRACT_DATE_REQUIRED_MESSAGE);

/**
 * Validate the typed seat count against the bounds the SERVER declares.
 *
 * The three sentences are imported from `AdjustSeatCapacityControl`, not
 * rewritten: they describe the column's bounds, which are the same bounds on
 * either control, and the bounds themselves come from the create-organization
 * schema that both already share. A per-dialog re-wording would be two sentences
 * for one rule.
 *
 * The FLOOR is not checked here — see the header. Returns the message to show,
 * or `null` when the value may be sent.
 */
function validateSeats(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return SEAT_CAPACITY_MIN_MESSAGE;
  // Parsed off the STRING, not through `Number()` alone: `Number("3.5")` and
  // `Number(" 3 ")` both survive a `>= 1` check.
  if (!/^-?\d+$/.test(trimmed)) return SEAT_CAPACITY_WHOLE_NUMBER_MESSAGE;

  const value = Number(trimmed);
  if (value < SEAT_CAPACITY_MIN) return SEAT_CAPACITY_MIN_MESSAGE;
  if (value > SEAT_CAPACITY_MAX) return SEAT_CAPACITY_MAX_MESSAGE;
  return null;
}

const TABULAR = "[font-variant-numeric:tabular-nums]";

function ReadOnlyFigure({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-sm text-[color:var(--text-tertiary)]">{label}</span>
      <span className={`text-md font-bold ${TABULAR}`}>{value}</span>
    </div>
  );
}

export function RenewContractControl({
  organizationId,
  contractEndDate,
  currentTermStartedAt,
  seatCapacity,
  activeSeatCount,
}: {
  organizationId: string;
  /** The CLOSING term's end date. Non-null — `canRenewContract` gates on it. */
  contractEndDate: string;
  /**
   * Where the closing term began, as the SERVER resolved it — its fallback to
   * the relationship start is already applied (`_shared/term.py ::
   * resolve_current_term_start`). Null only when the organization has no start
   * date either, and then this dialog says "Not set".
   *
   * There is deliberately no `contractStartDate` prop beside it any more: this
   * component re-derived the fallback in the browser until Story 21.5, which
   * meant two implementations of one rule with no way for the browser's to
   * learn that the server's had changed (DW-737).
   */
  currentTermStartedAt: string | null;
  seatCapacity: number;
  /**
   * The floor, read at dialog open. Shown, never enforced client-side.
   *
   * OPTIONAL, mirroring `AdminOrgDetail.active_seat_count` — the field arrived
   * in Story 21.4, later than the surfaces reading it, so an API from before
   * that deploy omits it (Rule 06 skew). `strictNullChecks` is OFF in this
   * project's `tsconfig`, so nothing here is compiler-enforced: the two reads
   * below guard by hand.
   */
  activeSeatCount?: number;
}) {
  const [open, setOpen] = useState(false);
  const [dateDraft, setDateDraft] = useState("");
  const [seatsDraft, setSeatsDraft] = useState(String(seatCapacity));
  const [dateError, setDateError] = useState<string | null>(null);
  const [seatsError, setSeatsError] = useState<string | null>(null);
  const [banner, setBanner] = useState<AdminOrgBanner | null>(null);
  const renewContract = useRenewContract(organizationId);

  function openDialog() {
    // THE DATE OPENS EMPTY, unlike `ExtendContractControl`, which pre-fills with
    // the stored value. Pre-filling here would seed the field with the one date
    // guaranteed to be refused — the closing term's own end date is never
    // strictly after itself — so the dialog would ship a built-in ORG-001.
    setDateDraft("");
    // The seat count opens at the CURRENT capacity: "same as last term" is the
    // common renewal and the one worth costing a keystroke. Re-seeded on every
    // open, so a cancelled edit never becomes the next edit's starting point.
    setSeatsDraft(String(seatCapacity));
    setDateError(null);
    setSeatsError(null);
    setBanner(null);
    setOpen(true);
  }

  /**
   * THE DIALOG DOES NOT CLOSE WHILE THE RENEWAL IS IN FLIGHT.
   *
   * Every dismissal funnels through here — Escape and outside click arrive as
   * `onOpenChange(false)`, the footer's Cancel calls it directly — for the same
   * reason `onSubmit` refuses re-entry: this is the one write on the card that
   * no endpoint can put back, and ORG-022 refuses it against a headcount only
   * the server can measure. A dialog unmounted mid-flight takes that refusal
   * down with it and the operator is told nothing at all about an irreversible
   * write they have already started.
   *
   * The guard cannot LOCK the dialog, because `useRenewContract` pins
   * `networkMode: "always"`. Under the default `"online"` an offline submit
   * paused the mutation in `pending` forever and every exit above was gated on
   * a flag that never cleared; `"always"` makes the offline case settle as a
   * transport error, which clears `isPending` and releases this guard.
   *
   * `onSuccess` still closes with `setOpen(false)` directly rather than through
   * here — but NOT because the guard would trap it. It would not:
   * `MutationObserver.onMutationUpdate` calls `#updateResult()` BEFORE
   * `#notify(action)`, so the status is already `success` and `isPending`
   * already `false` by the time the mutate-level `onSuccess` runs; and
   * `useMutation` returns a fresh `{ ...result }` per render, so the
   * `isPending` this closure reads is that render's snapshot — always `false`,
   * since `onSubmit` refuses to fire at all from a render where it is `true`.
   * The reason is simpler: a close on success is not a DISMISSAL. This guard
   * exists to refuse dismissals, and the terminal step of the write has no
   * business consulting it — widen it later (to hold the dialog open on an
   * unread refusal, say) and a success would silently strand an irreversible
   * write on screen.
   */
  function closeDialog() {
    if (renewContract.isPending) return;
    setOpen(false);
  }

  function onSubmit() {
    // The re-entrancy guard. `disabled` alone loses the race on a double-click
    // that lands before React re-renders — and this is the one write on the card
    // that no endpoint can put back.
    if (renewContract.isPending) return;

    const parsedDate = endDateShape.safeParse(dateDraft);
    const seatsMessage = validateSeats(seatsDraft);
    const dateMessage = parsedDate.success
      ? null
      : (parsedDate.error.issues[0]?.message ?? RENEW_CONTRACT_DATE_REQUIRED_MESSAGE);

    // BOTH are evaluated before either is reported. Returning on the first
    // failure would hide the second until the first is fixed.
    if (dateMessage !== null || seatsMessage !== null) {
      setDateError(dateMessage);
      setSeatsError(seatsMessage);
      setBanner(null);
      return;
    }

    setDateError(null);
    setSeatsError(null);
    setBanner(null);
    renewContract.mutate(
      { contract_end_date: parsedDate.data, seat_capacity: Number(seatsDraft.trim()) },
      {
        onSuccess: () => setOpen(false),
        onError: (error) => {
          // Keyed on the CODE through the shared mapper, on this control's OWN
          // surface — ORG-001 means something different here than it does on
          // the extend control, and `renew-contract` is what says so. The typed
          // values stay in the inputs: the operator has to see what refused.
          const mapped = toAdminOrgError(error, "renew-contract");
          setDateError(mapped.fieldErrors.contractEndDate ?? null);
          setSeatsError(mapped.fieldErrors.seatCapacity ?? null);
          setBanner(mapped.banner);
        },
      },
    );
  }

  // The closing term's start, EXACTLY as the server resolved it. The client
  // used to fall back to `contractStartDate` here, which was a second
  // derivation of a value the API already derives once
  // (`organizations/_shared/term.py :: resolve_current_term_start`) — two
  // implementations of one rule, and the browser's had no way of knowing when
  // the server's changed. Story 21.5 deleted it (DW-737): whatever the server
  // sends is what this dialog shows, and a null renders as "Not set".
  const termStart = currentTermStartedAt;
  const requestedSeats = /^\d+$/.test(seatsDraft.trim()) ? Number(seatsDraft.trim()) : null;
  // The headroom projection needs BOTH numbers. Without the floor the
  // subtraction is `n - undefined` = `NaN`, which fails every comparison below
  // and would silently withhold AC27's two states rather than saying why — so
  // the absence is made explicit and takes the same "render neither" branch a
  // half-typed seat count already takes.
  const projectedFree =
    requestedSeats === null || typeof activeSeatCount !== "number"
      ? null
      : requestedSeats - activeSeatCount;

  return (
    <>
      <Button variant="secondary" size="sm" onClick={openDialog}>
        {RENEW_CONTRACT_TRIGGER_LABEL}
      </Button>

      <Dialog open={open} onOpenChange={(next) => (next ? openDialog() : closeDialog())}>
        <DialogContent
          className="w-[min(var(--width-md,520px),100%)]"
          // The whole modal region is updating while the write is in flight, and
          // that is WHY its controls are inert and Escape does nothing. Same
          // treatment the loading regions across the app already get.
          aria-busy={renewContract.isPending}
        >
          <DialogHeader>
            <DialogTitle>{RENEW_CONTRACT_TRIGGER_LABEL}</DialogTitle>
            <DialogDescription>{RENEW_CONTRACT_DESCRIPTION}</DialogDescription>
          </DialogHeader>

          {/* The two read-only facts the operator needs to choose a seat count:
              which term is closing, and how many people are in it. The floor is
              shown BEFORE the field it constrains — showing it is what makes the
              ORG-022 refusal predictable rather than surprising (AC26). */}
          <div className="flex flex-wrap gap-6">
            <ReadOnlyFigure
              label={RENEW_CONTRACT_CURRENT_TERM_LABEL}
              value={`${termStart ? formatDate(termStart) : "Not set"} \u2013 ${formatDate(
                contractEndDate,
              )}`}
            />
            {/* GUARDED. This figure is the renewal FLOOR, on a dialog that
                performs an irreversible write, so the one thing it must not do
                is come up blank: an operator reading an empty floor has no way
                to tell "nobody is carrying a seat" from "the number did not
                arrive". The em-dash says the second. Same convention as Screen
                23's cells and Screen 24's tiles (`EMPTY_CELL`). */}
            <ReadOnlyFigure
              label={RENEW_CONTRACT_CARRIED_LABEL}
              value={typeof activeSeatCount === "number" ? activeSeatCount : EMPTY_CELL}
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="renew-contract-end-date">{RENEW_CONTRACT_END_DATE_LABEL}</Label>
            <Input
              id="renew-contract-end-date"
              type="date"
              value={dateDraft}
              aria-invalid={dateError ? true : undefined}
              aria-describedby={dateError ? "renew-contract-end-date-error" : undefined}
              onChange={(event) => {
                setDateDraft(event.target.value);
                setDateError(null);
              }}
            />
            {dateError ? (
              <p
                id="renew-contract-end-date-error"
                role="alert"
                className="text-sm text-[color:var(--text-error)]"
              >
                {dateError}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="renew-contract-seats">{RENEW_CONTRACT_SEATS_LABEL}</Label>
            <Input
              id="renew-contract-seats"
              // `inputMode` rather than `type="number"`: the spinner a number
              // input grows is a step control, and the sibling seat field
              // refuses one for the same reason.
              inputMode="numeric"
              autoComplete="off"
              className={TABULAR}
              value={seatsDraft}
              aria-invalid={seatsError ? true : undefined}
              aria-describedby={seatsError ? "renew-contract-seats-error" : undefined}
              onChange={(event) => {
                setSeatsDraft(event.target.value);
                setSeatsError(null);
              }}
            />
            {seatsError ? (
              <p
                id="renew-contract-seats-error"
                role="alert"
                className="text-sm text-[color:var(--text-error)]"
              >
                {seatsError}
              </p>
            ) : null}
            {/* AC27's headroom / full-pool states. Nothing is rendered BELOW the
                floor: the client does not own that refusal, and projecting a
                negative free count would be this file inventing ORG-022's
                answer a beat before the server gives it. */}
            {projectedFree !== null && projectedFree > 0 ? (
              <p className="text-sm text-[color:var(--text-tertiary)]">
                {renewalHeadroomMessage(projectedFree)}
              </p>
            ) : null}
            {projectedFree === 0 ? (
              <p className="text-sm text-[color:var(--text-tertiary)]">
                {RENEWAL_FULL_POOL_MESSAGE}
              </p>
            ) : null}
          </div>

          <p className="text-sm text-[color:var(--text-tertiary)]">
            {RENEW_CONTRACT_CONFIRMATION}
          </p>

          {banner ? (
            <Banner variant={banner.variant} title={banner.title}>
              <p>{banner.body}</p>
              {banner.code ? (
                <p className="text-sm">{`Reference code: ${banner.code}`}</p>
              ) : null}
            </Banner>
          ) : null}

          {/* THE PENDING LOCK, SPOKEN. Mounted with the dialog and filled only
              while in flight — a live region inserted at the same instant as its
              text is announced unreliably, so the region has to already be
              there. `role="status"` + polite + atomic is the shape `SyncLedger`
              already uses for its own delta announcer. */}
          <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {renewContract.isPending ? RENEW_CONTRACT_PENDING_ANNOUNCEMENT : ""}
          </p>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={closeDialog}
              disabled={renewContract.isPending}
            >
              Cancel
            </Button>
            {/* `aria-busy` on the control that is waiting, matching
                `SubscriptionSection`'s checkout button and `LegacySignUpScreen`'s
                submit: native `disabled` is the true "cannot act right now", and
                `aria-busy` is what says it is momentary rather than refused. */}
            <Button
              onClick={onSubmit}
              disabled={renewContract.isPending}
              aria-busy={renewContract.isPending}
            >
              {renewContract.isPending
                ? RENEW_CONTRACT_PENDING_LABEL
                : RENEW_CONTRACT_SUBMIT_LABEL}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
